/**
 * Buffer circular de amostras de áudio com índices ABSOLUTOS e monotônicos.
 *
 * O índice absoluto (`posicao`) é o total de amostras já escritas desde o início — ele nunca
 * volta atrás, mesmo quando o armazenamento interno dá a volta. É isso que permite pedir
 * "de 300 ms antes do disparo até agora" sem ambiguidade: o chamador converte tempo em índice
 * de amostra e pede a janela, sem precisar saber onde o buffer está fisicamente.
 */
export class AudioRingBuffer {
  private readonly dados: Float32Array;
  private escritas = 0;

  constructor(capacidadeAmostras: number) {
    if (capacidadeAmostras <= 0) throw new Error('capacidadeAmostras deve ser > 0');
    this.dados = new Float32Array(capacidadeAmostras);
  }

  /** Total de amostras já escritas; índice absoluto da próxima amostra. */
  get posicao(): number {
    return this.escritas;
  }

  get capacidade(): number {
    return this.dados.length;
  }

  escrever(frame: Float32Array): void {
    const cap = this.dados.length;
    for (let i = 0; i < frame.length; i++) {
      this.dados[(this.escritas + i) % cap] = frame[i];
    }
    this.escritas += frame.length;
  }

  /**
   * Extrai `[de, ate)` em índices absolutos. Se `de` já saiu da janela, clampa no mais antigo
   * ainda disponível — perder o começo é melhor que estourar.
   */
  extrair(de: number, ate: number): Float32Array {
    const cap = this.dados.length;
    const maisAntigo = Math.max(0, this.escritas - cap);
    const inicio = Math.max(de, maisAntigo);
    const fim = Math.min(ate, this.escritas);
    if (fim <= inicio) return new Float32Array(0);

    const saida = new Float32Array(fim - inicio);
    for (let i = 0; i < saida.length; i++) {
      saida[i] = this.dados[(inicio + i) % cap];
    }
    return saida;
  }

  limpar(): void {
    this.escritas = 0;
    this.dados.fill(0);
  }
}
